import { LogoutOutlined } from '@ant-design/icons'
import { Avatar, Button, Flex, Image, Layout, LayoutProps, Row, Tooltip } from 'antd'
import { connect } from 'dva'
import { createContext, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import LogoDark from 'Assets/Images/SkitLogoDark.svg'
import LogoLight from 'Assets/Images/SkitLogoLight.svg'
import { getUserInitials } from 'Utils/getUserInitials'
import { GLOBAL_SIDE_NAV_ID } from 'Utils/isGlobalSideNavCollapsed'
import { SkitLogo } from 'Components/Icons/Logo/SkitLogo'
import {
  BrandText,
  ContentLayout,
  FoldOutlined,
  HomeRow,
  LogoContainer,
  LogoSpace,
  LogoutButton,
  MenuDivider,
  MenuRow,
  MenuTitle,
  PageMenu,
  UnfoldOutlined,
  UserName,
  UserRow,
} from 'Layouts/styled'

type ReduxProps = {
  dispatch: any
  auth: any
}
type AuthBaseLayoutProps = {
  menu: React.ReactNode
  bottomMenu: React.ReactNode
  headerRender?: () => React.ReactNode
  sideNavTheme?: 'light' | 'dark'
  defaultCollapsed?: boolean
  sidePanelProps?: any
  sideMenuProps?: any
  collapsibleSideNav?: boolean
} & ReduxProps

const defaultHeaderRender = ({ theme, collapsed }: any) => {
  return (
    <HomeRow theme={theme}>
      <SkitLogo width={25} height={25} />
      {!collapsed ? <MenuTitle theme={theme}>Skit.ai</MenuTitle> : null}
    </HomeRow>
  )
}

export const GlobalNavigationContext = createContext({
  collapsed: false,
})

const AuthBaseLayout: React.FC<LayoutProps & AuthBaseLayoutProps> = ({
  dispatch,
  menu,
  bottomMenu,
  sideNavTheme = 'light',
  sidePanelProps = {},
  sideMenuProps = {},
  defaultCollapsed = false,
  headerRender = defaultHeaderRender,
  auth,
  children = 'Content goes here',
  collapsibleSideNav = true, // Discussed it with product team. They are ok with default value to true.
}) => {
  const user = auth?.user
  const [collapsed, setCollapsed] = useState<boolean>(defaultCollapsed)
  const navigate = useNavigate()
  const isDark = sideNavTheme === 'dark'
  const { style: sideMenuStyles, ...restSideMenuProps } = sideMenuProps
  const { style: sidePanelStyles, ...restSidePanelProps } = sidePanelProps

  const handleCollapse = (val: boolean) => {
    if (!collapsibleSideNav) return
    const aside = document.getElementById(GLOBAL_SIDE_NAV_ID)
    if (aside) {
      aside.setAttribute('data-collapsed', '' + val)
    }
    setCollapsed(val)
  }

  const logout = () =>
    dispatch({
      type: 'auth/userSignOut',
      onSuccess: () => navigate('/login'),
    })

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleCollapse(defaultCollapsed) // magic :)
    })
    return () => clearTimeout(timeout)
  }, [defaultCollapsed])

  return (
    <GlobalNavigationContext.Provider
      value={{
        collapsed,
      }}
    >
      <Layout
        style={{
          height: '100vh',
          background: 'linear-gradient(90deg, #EEF8FF 0%, #E9FFF6 100%)',
        }}
      >
        <Layout.Sider
          id={GLOBAL_SIDE_NAV_ID}
          data-collapsed={collapsed ? 'true' : 'false'}
          trigger={null}
          width={250}
          collapsedWidth={50}
          collapsed={collapsed}
          collapsible={collapsibleSideNav}
          onCollapse={() => handleCollapse(!collapsed)}
          style={{
            backgroundColor: 'transparent',
            ...sidePanelStyles,
          }}
          {...restSidePanelProps}
        >
          <LogoContainer collapsed={collapsed ? 'true' : 'false'} theme={sideNavTheme}>
            {headerRender({
              theme: sideNavTheme,
              collapsed,
            })}
            {collapsibleSideNav ? (
              <Button
                type="text"
                onClick={() => handleCollapse(!collapsed)}
                style={{
                  margin: '0.5rem 0',
                  display: 'grid',
                  placeItems: 'center',
                }}
                icon={
                  collapsed ? (
                    <UnfoldOutlined theme={sideNavTheme} />
                  ) : (
                    <FoldOutlined theme={sideNavTheme} />
                  )
                }
              />
            ) : null}
          </LogoContainer>
          <PageMenu
            theme={sideNavTheme}
            mode="inline"
            collapsed={collapsed.toString()}
            {...restSideMenuProps}
          >
            <Flex vertical justify="space-between" style={{ height: '100%' }}>
              <div>{menu}</div>
              <div>{bottomMenu}</div>
            </Flex>
          </PageMenu>
          <MenuRow theme={sideNavTheme}>
            <UserRow theme={sideNavTheme}>
              {!collapsed ? (
                <Row style={{ padding: '4px' }}>
                  <Avatar style={{ backgroundColor: user?.profile_color }} size="small">
                    {getUserInitials(user?.first_name, user?.last_name, user?.email)}
                  </Avatar>
                  <Tooltip title={user?.email}>
                    <UserName ellipsis theme={sideNavTheme}>
                      {user?.email}
                    </UserName>
                  </Tooltip>
                </Row>
              ) : null}
              <Tooltip title="Logout">
                <LogoutButton
                  type="text"
                  theme={sideNavTheme}
                  icon={<LogoutOutlined />}
                  onClick={logout}
                />
              </Tooltip>
            </UserRow>
            <LogoSpace size={'small'} theme={sideNavTheme}>
              {collapsed ? (
                <SkitLogo style={{ marginTop: '8px' }} />
              ) : (
                <>
                  <BrandText theme={sideNavTheme}>Powered By</BrandText>
                  <Image preview={false} width={80} src={isDark ? LogoLight : LogoDark} />
                </>
              )}
            </LogoSpace>
          </MenuRow>
        </Layout.Sider>
        <ContentLayout collapsed={collapsed.toString()}>{children}</ContentLayout>
      </Layout>
    </GlobalNavigationContext.Provider>
  )
}

export default connect(({ auth }: any) => ({ auth }))(AuthBaseLayout)
